import React, { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import styles from './PatientInsuranceList.module.css';

/** "2027-03-31" -> "31/03/2027" (a API devolve a validade em ISO) */
const formatDate = (iso) => {
    if (!iso) return '';
    const [year, month, day] = String(iso).slice(0, 10).split('-');
    return day && month && year ? `${day}/${month}/${year}` : iso;
};

const EXPIRING_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Situação da validade, no mesmo tom dos avisos da agenda: vencida (vermelho),
 * vence nos próximos 30 dias (âmbar) ou só a data. Conta em dias de calendário locais.
 */
const validityInfo = (iso) => {
    const [year, month, day] = String(iso).slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return { text: `Vence em ${formatDate(iso)}`, tone: null };

    const today = new Date();
    const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    const days = Math.round((Date.UTC(year, month - 1, day) - todayUTC) / DAY_MS);
    if (days < 0) return { text: `Venceu em ${formatDate(iso)}`, tone: 'expired' };
    if (days === 0) return { text: 'Vence hoje', tone: 'expiring' };
    if (days <= EXPIRING_DAYS) {
        const left = days === 1 ? 'falta 1 dia' : `faltam ${days} dias`;
        return { text: `Vence em ${formatDate(iso)} (${left})`, tone: 'expiring' };
    }
    return { text: `Vence em ${formatDate(iso)}`, tone: null };
};

const isActive = (card) => Number(card.active ?? 1) === 1;

/** Ação compacta da linha: ícone com tooltip (o span mantém o tooltip no botão desligado). */
function RowAction({ title, label, onClick, Icon, tone }) {
    return (
        <Tooltip title={title} arrow>
            <span>
                <IconButton
                    size="small"
                    className={`${styles.rowAction} ${tone ? styles[tone] : ''}`}
                    onClick={onClick}
                    aria-label={label}
                >
                    <Icon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
}

/** Uma carteirinha: operadora e plano, número, titular, validade, avisos e ações. */
function CardRow({ card, onEdit, onInactivate, onReactivate }) {
    const active = isActive(card);
    const providerInactive = card.provider_active !== undefined && Number(card.provider_active) === 0;
    const title = [card.provider_name, card.plan_name].filter(Boolean).join(' · ');
    // Destaque só na carteirinha ativa: a inativa já não é oferecida ao agendar
    const validity = card.valid_until ? validityInfo(card.valid_until) : null;
    const validityTone = active && validity?.tone ? styles[validity.tone] : '';

    return (
        <li className={`${styles.row} ${active ? '' : styles.rowInactive}`}>
            <span className={`${styles.icon} ${active ? '' : styles.iconInactive}`}>
                <BadgeOutlinedIcon fontSize="small" />
            </span>

            <div className={styles.texts}>
                <div className={styles.titleLine}>
                    <span className={styles.title} title={title}>{title}</span>
                    {!active && <span className={`${styles.tag} ${styles.tagMuted}`}>Inativa</span>}
                    {providerInactive && <span className={`${styles.tag} ${styles.tagWarning}`}>Operadora inativa</span>}
                </div>
                <div className={styles.meta}>
                    <span className={`${styles.metaItem} ${styles.number}`}>{card.card_number}</span>
                    {card.holder_name && (
                        <span className={styles.metaItem}>
                            <PersonOutlineOutlinedIcon />
                            Titular: {card.holder_name}
                        </span>
                    )}
                    {validity && (
                        <span className={`${styles.metaItem} ${validityTone ? `${styles.validityAlert} ${validityTone}` : ''}`}>
                            <EventOutlinedIcon />
                            {validity.text}
                        </span>
                    )}
                </div>
            </div>

            {(onEdit || onInactivate || onReactivate) && (
                <span className={styles.actions}>
                    {onEdit && active && (
                        <RowAction
                            title="Editar carteirinha"
                            label={`Editar carteirinha ${card.card_number}`}
                            onClick={() => onEdit(card)}
                            Icon={EditOutlinedIcon}
                        />
                    )}
                    {active ? (
                        onInactivate && (
                            <RowAction
                                title="Inativar carteirinha"
                                label={`Inativar carteirinha ${card.card_number}`}
                                onClick={() => onInactivate(card)}
                                Icon={PowerSettingsNewOutlinedIcon}
                                tone="warning"
                            />
                        )
                    ) : (
                        onReactivate && (
                            <RowAction
                                title={providerInactive ? 'A operadora está inativa; ative a operadora em Cadastros > Convênios primeiro' : 'Reativar carteirinha'}
                                label={`Reativar carteirinha ${card.card_number}`}
                                onClick={providerInactive ? undefined : () => onReactivate(card)}
                                Icon={RestoreOutlinedIcon}
                                tone="positive"
                            />
                        )
                    )}
                </span>
            )}
        </li>
    );
}

/**
 * Carteirinhas da pessoa: ativas em destaque, inativas recolhidas em "Ver inativas".
 * Props: { cards, onEdit(card), onToggleActive(card, nextActive) }. Inativar pede
 * uma confirmação simples; reativar segue direto. Nunca exclui.
 */
export default function PatientInsuranceList({ cards = [], onEdit, onToggleActive }) {
    const [showInactive, setShowInactive] = useState(false);
    const [inactivateConfirm, setInactivateConfirm] = useState(null);

    const activeCards = cards.filter(isActive);
    const inactiveCards = cards.filter((card) => !isActive(card));

    const handlers = {
        onEdit,
        onInactivate: onToggleActive ? (card) => setInactivateConfirm(card) : undefined,
        onReactivate: onToggleActive ? (card) => onToggleActive(card, 1) : undefined
    };

    return (
        <>
            <div className={styles.list}>
                {activeCards.length > 0 && (
                    <ul className={styles.group}>
                        {activeCards.map((card) => (
                            <CardRow key={card.id} card={card} {...handlers} />
                        ))}
                    </ul>
                )}

                {inactiveCards.length > 0 && (
                    <>
                        <button type="button" className={styles.inactiveToggle} onClick={() => setShowInactive((v) => !v)}>
                            {showInactive ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                            {showInactive ? 'Ocultar inativas' : `Ver inativas (${inactiveCards.length})`}
                        </button>
                        {showInactive && (
                            <ul className={styles.group}>
                                {inactiveCards.map((card) => (
                                    <CardRow key={card.id} card={card} {...handlers} />
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            <ConfirmPopup
                Visibility={!!inactivateConfirm}
                Title="Inativar carteirinha"
                Description={`A carteirinha ${inactivateConfirm?.card_number || ''} (${inactivateConfirm?.provider_name || ''}) sai das opções ao agendar, mas os atendimentos já marcados continuam com ela. Dá para reativar depois.`}
                ConfirmLabel="Inativar"
                OnConfirm={() => onToggleActive(inactivateConfirm, 0)}
                OnHide={() => setInactivateConfirm(null)}
            />
        </>
    );
}
